import { call, put, takeLatest } from 'redux-saga/effects';
import Swal from 'sweetalert2';

import * as types from './UserActionType';
import * as actions from './UserActions';
import * as userService from './UserServices';
import { convertStreamToBlob, downloadBlobUrl } from '../../../utils/fileDownloads';
import SweetAlert from '../../../components/sweetalert/SweetAlert';

function* downloadImportedFileSaga(action) {
    try {
        // Call API to get file (expecting base64 and fileName in response)
        const response = yield call(userService.downloadImportedFile, action.payload);
        const base64 = response?.data?.fileBytes || response?.data?.file || response?.data || null;
        const fileName = response?.data?.fileName || action.payload.fileName || 'imported_file.xlsx';
        if (base64) {
            // Download logic
            const byteCharacters = atob(base64);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            yield put(actions.downloadImportedFileSuccess({ fileName }));
        } else {
            yield put(actions.downloadImportedFileFailure("No file data found"));
            yield call(() =>
                Swal.fire({
                    icon: "error",
                    text: "File download failed: No file data found.",
                    confirmButtonText: "OK",
                })
            );
        }

    } catch (error) {
        yield put(actions.downloadImportedFileFailure(error));
        yield call(() =>
            Swal.fire({
                icon: "error",
                text: "File download failed. Please try again.",
                confirmButtonText: "OK",
            })
        );
    }
}

function* fetchFileImportDetailsSaga(action) {
    try {
        const payloadData = {
            ...action.payload,
            code: ''
        }
        const response = yield call(userService.fetchFileImportDetails, payloadData);
        // Expecting response.data to have { fileImportDetailsList, fileImportTotalRecords }
        yield put(actions.fetchFileImportDetailsSuccess(response.data));
    } catch (error) {
        yield put(actions.fetchFileImportDetailsFailure(error));
    }
}

function* fetchUserSaga(action) {
    try {
        const response = yield call(userService.fetchUser, action.payload);
        yield put(actions.getUsersSuccess(response.data));
    } catch (error) {
        yield put(actions.getUsersFailure(error));
    }
}

function* deleteUserSaga(action) {
    try {
        const response = yield call(userService.deleteUser, action.payload);
        yield put(actions.deleteUserSuccess(response.data));
        yield put(actions.getUsersRequest({ PageNumber: 1, PageSize: 10 })); // Refresh user list after deletion
    } catch (error) {
        yield put(actions.deleteUserFailure(error));
    }
}

function* saveUserSaga(action) {
    try {
        const { data, navigate,isSilent } = action.payload;
        const response = yield call(userService.saveUser, data);
        if (response.status) {
            yield put(actions.saveUserSuccess(response.data));
            yield put(actions.getUsersRequest({ PageNumber: 1, PageSize: 10 }));
            
            // Show success message based on whether it's create or update
            const isUpdate = data.UserId && data.UserId > 0;
            const isAuthorizedUser = data.IsAuthorizedUser === true;
            // New user: the API message also says what happened to the welcome email (sent / not sent / failed)
            const successText = isUpdate
                ? (isAuthorizedUser ? 'Authorized user added successfully' : 'User updated successfully')
                : response.responseMessage || (isAuthorizedUser ? 'Authorized user added successfully' : 'User saved successfully');
        if (!isSilent) {
            yield call(() =>
                SweetAlert.success({
                    title: 'Success!',
                    text: successText,
                    allowOutsideClick: true,
                    allowEscapeKey: true,
                    confirmButtonColor: '#28a745',
                }));}
            if (navigate) {
            navigate(-1);
            }// Navigate back after successful save
        } else {
            yield put(actions.saveUserFailure(response.responseMessage));
            yield call(() =>
                SweetAlert.info({
                    title: 'Information',
                    text: response.responseMessage || 'User already exists.',
                    allowOutsideClick: true,
                    allowEscapeKey: true,
                    confirmButtonColor: '#3085d6',
                }))
        }
    } catch (error) {
        yield put(actions.saveUserFailure(error));
    }
}

function* getUserByIdSaga(action) {
    try {
        const response = yield call(userService.fetchUserById, action.payload);
        yield put(actions.getUserByIdSuccess(response.data));
    } catch (error) {
        yield put(actions.getUserByIdFailure(error));
    }
}
function* exportUserDataSaga(action) {
    try {
        const response = yield call(userService.exportUserData, action.payload);

        const base64 =
            response?.data?.fileBytes ||
            response?.data?.file ||        
            response?.data ||              
            response?.fileBytes ||        
            null;

        if (base64) {
            yield put(actions.exportUserDataSuccess(base64));
        } else {
            yield put(actions.exportUserDataFailure("Invalid export response"));
            console.error("Export error: No base64 found in API response", response);
        }

    } catch (error) {
        yield put(actions.exportUserDataFailure(error));
    }
}

function* importUserDataSaga(action) {
    try {
        const { activeTab } = action.payload;
        const response = yield call(userService.importUserData, action.payload.formData);
        if (response?.status) {
            const cleanedMessage = response?.responseMessage || "Data Imported Successfully.";
            Swal.fire({
                icon: "success",
                text: cleanedMessage,
                confirmButtonText: "OK",
                timer: 3000
            });
            yield put(actions.importUserDataSuccess(response.data));

            if (activeTab === 'fileImportDetails') {
                const fileImportPayload = {
                    pageNumber: 1,
                    pageSize: 10,
                    searchText: '',
                    sortColumn: 'FileImportMasterID',
                    sortColumnDir: 'DESC',
                    code: ''
                };
                const fileResponse = yield put(actions.fetchFileImportDetailsRequest(fileImportPayload));
                yield put(actions.fetchFileImportDetailsSuccess(fileResponse.data));
            } else {
                const userResponse = yield call(userService.fetchUser, action.payload);
                yield put(actions.getUsersSuccess(userResponse.data));
            }
        }
        else if (response?.status === false) {

            const cleanedMessage = response?.data?.replace("System.Exception:", "")?.trim();
            Swal.fire({
                icon: "error",
                text: cleanedMessage || response?.responseMessage || "An error occurred during data import.",
                confirmButtonText: "OK",
                timer: 3000
            });
            yield put(actions.importUserDataFailure(response?.data));
        }
       
    } catch (error) {
        yield put(actions.importUserDataFailure(error));
    }
}

function* fetchUserActivitySaga(action) {
    try {
        const response = yield call(userService.fetchUserActivity, action.payload);
        // API returns { status, responseMessage, data: { data: [...], totalRecords } }
        const activityData = response?.data?.data || [];
        const totalRecords = response?.data?.totalRecords || 0;
        yield put(actions.getUserActivitySuccess({ 
            activityList: activityData, 
            totalCount: totalRecords 
        }));
    } catch (error) {
        yield put(actions.getUserActivityFailure(error));
    }
}
function* resetMfaSaga(action) {
    try {
        const response = yield call(userService.resetMfa, action.payload);
        if (response.status) {
            yield put(actions.resetMFASuccess(response.data));
            yield call(() =>
                SweetAlert.success({
                    title: 'Success!',
                    text: response.responseMessage || 'MFA has been reset successfully.',
                    allowOutsideClick: true,
                    allowEscapeKey: true,
                    confirmButtonColor: '#28a745',
                })
            );
        } else {
            yield put(actions.resetMFAFailure(response.responseMessage));
            yield call(() =>
                SweetAlert.info({
                    title: 'Information',
                    text: response.responseMessage || 'MFA reset failed.',
                    allowOutsideClick: true,
                    allowEscapeKey: true,
                    confirmButtonColor: '#3085d6',
                })
            );
        }
    } catch (error) {
        yield put(actions.resetMFAFailure(error));
        yield call(() =>
            SweetAlert.error({
                title: 'Error',
                text: error?.message || 'MFA reset failed. Please try again.',
                allowOutsideClick: true,
                allowEscapeKey: true,
                confirmButtonColor: '#d33',
            })
        );
    }
}

// Sends the welcome email to every user whose password email never went out (Password_Sent = 0).
function* sendPendingWelcomeEmailsSaga(action) {
    try {
        const response = yield call(userService.sendPendingWelcomeEmails, action.payload);
        if (response.status) {
            yield put(actions.sendPendingWelcomeEmailsSuccess(response.data));
            yield call(() =>
                SweetAlert.success({
                    title: 'Success!',
                    text: response.responseMessage || 'Welcome emails sent successfully.',
                    allowOutsideClick: true,
                    allowEscapeKey: true,
                    confirmButtonColor: '#28a745',
                })
            );
        } else {
            yield put(actions.sendPendingWelcomeEmailsFailure(response.responseMessage));
            yield call(() =>
                SweetAlert.info({
                    title: 'Information',
                    text: response.responseMessage || 'Failed to send welcome emails.',
                    allowOutsideClick: true,
                    allowEscapeKey: true,
                    confirmButtonColor: '#3085d6',
                })
            );
        }
    } catch (error) {
        yield put(actions.sendPendingWelcomeEmailsFailure(error));
        yield call(() =>
            SweetAlert.error({
                title: 'Error',
                text: error?.message || 'Failed to send welcome emails. Please try again.',
                allowOutsideClick: true,
                allowEscapeKey: true,
                confirmButtonColor: '#d33',
            })
        );
    }
}

function* resendWelcomeEmailSaga(action) {
    try {
        const response = yield call(userService.resendWelcomeEmail, action.payload);
        if (response.status) {
            yield put(actions.resendWelcomeEmailSuccess(response.data));
            yield call(() =>
                SweetAlert.success({
                    title: 'Success!',
                    text: response.responseMessage || 'Welcome email resent successfully.',
                    allowOutsideClick: true,
                    allowEscapeKey: true,
                    confirmButtonColor: '#28a745',
                })
            );
        } else {
            yield put(actions.resendWelcomeEmailFailure(response.responseMessage));
            yield call(() =>
                SweetAlert.info({
                    title: 'Information',
                    text: response.responseMessage || 'Failed to resend welcome email.',
                    allowOutsideClick: true,
                    allowEscapeKey: true,
                    confirmButtonColor: '#3085d6',
                })
            );
        }
    } catch (error) {
        yield put(actions.resendWelcomeEmailFailure(error));
        yield call(() =>
            SweetAlert.error({
                title: 'Error',
                text: error?.message || 'Failed to resend welcome email. Please try again.',
                allowOutsideClick: true,
                allowEscapeKey: true,
                confirmButtonColor: '#d33',
            })
        );
    }
}


function* exportUserActivitySaga(action) {
    try {
        const response = yield call(userService.exportUserActivity, action.payload);
        const blobUrl = yield call(
            convertStreamToBlob,
            response,
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        );
        yield call(downloadBlobUrl, blobUrl, 'UserActivity.xlsx');
        yield put(actions.exportUserActivitySuccess(null));
    } catch (error) {
        yield put(actions.exportUserActivityFailure(error));
        console.error("Export user activity error:", error);
    }
}

export default function* watchUserSaga() {
    yield takeLatest(types.GET_USERS_REQUEST, fetchUserSaga);
    yield takeLatest(types.DELETE_USER_REQUEST, deleteUserSaga);
    yield takeLatest(types.SAVE_USER_REQUEST, saveUserSaga);
    yield takeLatest(types.GET_USER_BY_ID_REQUEST, getUserByIdSaga);
    yield takeLatest(types.RESET_MFA_REQUEST, resetMfaSaga);
    yield takeLatest(types.RESEND_WELCOME_EMAIL_REQUEST, resendWelcomeEmailSaga);
    yield takeLatest(types.SEND_PENDING_WELCOME_EMAILS_REQUEST, sendPendingWelcomeEmailsSaga);
    yield takeLatest(types.EXPORT_USER_REQUEST, exportUserDataSaga);
    yield takeLatest(types.IMPORT_USER_REQUEST, importUserDataSaga);
    yield takeLatest(types.FETCH_FILE_IMPORT_DETAILS_REQUEST, fetchFileImportDetailsSaga);
    yield takeLatest(types.DOWNLOAD_IMPORTED_FILE_REQUEST, downloadImportedFileSaga);
    yield takeLatest(types.GET_USER_ACTIVITY_REQUEST, fetchUserActivitySaga);
    yield takeLatest(types.EXPORT_USER_ACTIVITY_REQUEST, exportUserActivitySaga);
    yield takeLatest(types.GET_USER_AUTHORIZED_MAPPING_REQUEST, fetchUserAuthorizedMappingSaga);
    yield takeLatest(types.TOGGLE_USER_AUTHORIZED_MAPPING_REQUEST, toggleUserAuthorizedMappingSaga);
}

function* fetchUserAuthorizedMappingSaga(action) {
    try {
        const response = yield call(userService.fetchUserAuthorizedMapping, action.payload);
        if (response.status) {
            yield put(actions.getUserAuthorizedMappingSuccess(response.data));
        } else {
            yield put(actions.getUserAuthorizedMappingFailure(response.responseMessage));
        }
    } catch (error) {
        yield put(actions.getUserAuthorizedMappingFailure(error));
    }
}

function* toggleUserAuthorizedMappingSaga(action) {
    try {
        const { data, onSuccess } = action.payload;
        const response = yield call(userService.toggleUserAuthorizedMapping, data);
        if (response.status) {
            yield put(actions.toggleUserAuthorizedMappingSuccess(response.data));
            SweetAlert.success({ text: response.responseMessage || (data.isAttach ? 'User attached successfully' : 'User detached successfully') });
            if (onSuccess) onSuccess();
        } else {
            yield put(actions.toggleUserAuthorizedMappingFailure(response.responseMessage));
            SweetAlert.warning(response.responseMessage || 'Operation failed');
        }
    } catch (error) {
        yield put(actions.toggleUserAuthorizedMappingFailure(error));
        SweetAlert.warning('Something went wrong');
    }
}